import {
  ApiTimeOffAccrualFrequencyTypes,
  ApiTimeOffDateType,
  ApiTimeOffPolicyAccrualAtType,
} from "@/types/time-off/time-off-policy.types.ts";
import { parseSalaryValue } from "@/helpers/utils.helper.ts";
import { SelectItem, SelectOptionItem, SelectValueType } from "@skailer-com/uikit";
import i18n from "i18next";
import { z } from "zod";

export function getStartAmountOptions(startDateType: ApiTimeOffDateType): SelectItem[] {
  const limits: Record<ApiTimeOffDateType, number> = {
    [ApiTimeOffDateType.day]: 120,
    [ApiTimeOffDateType.week]: 52,
    [ApiTimeOffDateType.month]: 24,
    [ApiTimeOffDateType.year]: 10,
  };

  const max = limits[startDateType] ?? limits[ApiTimeOffDateType.month];
  return Array.from({ length: max + 1 }, (_, i) => ({ id: String(i), title: String(i) }));
}

export const accrualWithDateTypes = [
  ApiTimeOffAccrualFrequencyTypes.semimonthly,
  ApiTimeOffAccrualFrequencyTypes.monthly,
  ApiTimeOffAccrualFrequencyTypes.semiannual,
  ApiTimeOffAccrualFrequencyTypes.yearly,
  ApiTimeOffAccrualFrequencyTypes.monthly_annual_proportional,
];

export const accrualWithTwoDateTypes = [
  ApiTimeOffAccrualFrequencyTypes.semimonthly,
  ApiTimeOffAccrualFrequencyTypes.semiannual,
];

export function getDayOfMonthOptions(): SelectOptionItem<SelectItem>[] {
  return [
    { id: 1, title: i18n.t("common:first_day_of_month") },
    { id: 31, title: i18n.t("common:last_day_of_month") },
    { id: "day-of-month-divider", type: "divider" },
    ...Array.from({ length: 31 }, (_, i) => ({ id: i + 1, title: (i + 1).toString() })),
  ];
}

export function createDayOfMonthChangeHandler(
  value: Date | null | undefined,
  onChange: (date: Date) => void
) {
  return (_: SelectValueType, entity: SelectItem | null) => {
    if (!entity) return;
    const selectedDay = Number(entity.id);
    const dateToUpdate = new Date(value || new Date());
    dateToUpdate.setMonth(0);
    dateToUpdate.setDate(selectedDay);
    onChange(dateToUpdate);
  };
}

export const buildMilestoneSchema = () =>
  z.object({
    title: z.string().default(""),
    start_date_type: z.enum(ApiTimeOffDateType),
    start_amount: z
      .union([z.string(), z.number()])
      .nullable()
      .optional()
      .transform(parseSalaryValue)
      .pipe(
        z
          .number({ error: i18n.t("errors:field_required") })
          .int(i18n.t("api_errors:must_be_integer"))
          .min(0, i18n.t("api_errors:must_be_positive"))
      ),
    start_balance_amount: z
      .union([z.string(), z.number()])
      .nullable()
      .optional()
      .transform(parseSalaryValue)
      .pipe(z.number()),
    accrual_frequency_type: z.enum(ApiTimeOffAccrualFrequencyTypes),
    accrual_frequency_data: z
      .object({
        date_first: z.date().nullable().optional(),
        date_second: z.date().nullable().optional(),
      })
      .nullable()
      .optional(),
    accrual_amount: z
      .union([z.string(), z.number()])
      .nullable()
      .optional()
      .transform(parseSalaryValue)
      .pipe(
        z
          .number({ error: i18n.t("errors:field_required") })
          .min(0, i18n.t("api_errors:must_be_positive"))
      ),
    accrual_at_type: z.enum(ApiTimeOffPolicyAccrualAtType),
    accrual_cap_enabled: z.boolean(),
    accrual_cap_amount: z
      .union([z.string(), z.number()])
      .nullable()
      .optional()
      .transform(parseSalaryValue)
      .pipe(z.number()),
  });

const startDateTypeInDays: Record<ApiTimeOffDateType, number> = {
  [ApiTimeOffDateType.day]: 1,
  [ApiTimeOffDateType.week]: 7,
  [ApiTimeOffDateType.month]: 30,
  [ApiTimeOffDateType.year]: 365,
};

export function getMilestoneStartOffset(milestone: {
  start_date_type: ApiTimeOffDateType;
  start_amount: number;
}): number {
  return (
    (startDateTypeInDays[milestone.start_date_type] ?? 0) * Number(milestone.start_amount || 0)
  );
}

// Нулевой майлстоун всегда первый: TimeOffPolicyAccrueSettings жёстко привязан к milestones.0.
export function sortMilestonesByStart<
  T extends { start_date_type: ApiTimeOffDateType; start_amount: number },
>(milestones: T[]): T[] {
  const [base, ...rest] = milestones;
  if (!base) return milestones;
  return [base, ...rest.sort((a, b) => getMilestoneStartOffset(a) - getMilestoneStartOffset(b))];
}
